import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import {
  buildFlatpickrOptions,
  DataAttributes,
} from "lib/util/build_flatpickr_options";
// Extends Decor's form_field controller (validation lifecycle, error rendering,
// etc.) — same base class as every Suite form-field controller.
import { FormFieldController } from "decor";

export default class DateCalendarController extends FormFieldController {
  // The input target is emitted by Suite::Forms::TextField under THIS
  // controller's identifier (the input element carries
  // `data-mercor--forms--date-calendar-target="input"` because Vident
  // generates target attrs against the calling component's stimulus_identifier).
  public static targets = ["input"];

  declare private readonly inputTarget: HTMLInputElement;

  private datePicker: Instance | null = null;
  private ticking = false;
  // Same reference for add/removeEventListener. MUST be a capture-phase window
  // listener: `scroll` does not bubble, so a bubble-phase window listener never
  // sees the field's own scroll container scroll (modal body, cart dropdown,
  // any `overflow:auto` ancestor) and the popup would detach on scroll. Capture
  // phase on window receives scroll from every descendant.
  private readonly onScroll = (): void => this.scrolled();

  public static values = {
    ...FormFieldController.values,
    data: Object,
  };

  declare private readonly dataValue: DataAttributes;

  public connect() {
    super.connect();
    const dataAttributes = this.dataValue;

    // When the input is inside an HTML5 <dialog>, flatpickr's default of
    // appending the calendar to <body> places it OUTSIDE the dialog's top
    // layer, so the dialog intercepts every click on a day cell. We therefore
    // append it INTO the dialog (buildFlatpickrOptions passes `appendTo: dialog`)
    // so day-cell clicks land in the top layer.
    //
    // It must NOT be `static`, though: static mode plants the calendar as a
    // child of the input wrapper, which sits inside the modal body's
    // `overflow-y:auto` and the purcon column's `overflow:hidden` — so the
    // bottom/right of the calendar gets clipped. Instead we switch the popup to
    // `position: fixed` (below), which escapes every overflow ancestor. A modal
    // page isn't scrolled, so flatpickr's page-relative top/left coincide with
    // viewport coordinates and the calendar still anchors to the input.
    const dialog = this.element.closest("dialog") as HTMLElement | null;

    const flatpickrOpts = buildFlatpickrOptions(
      dataAttributes,
      dialog ?? undefined,
    );
    this.datePicker = flatpickr(this.inputTarget, flatpickrOpts);
    if (dialog && this.datePicker) {
      this.datePicker.calendarContainer.style.position = "fixed";
    }

    window.addEventListener("scroll", this.onScroll, true);
  }

  public disconnect() {
    window.removeEventListener("scroll", this.onScroll, true);
    if (this.datePicker) {
      this.datePicker.destroy();
      this.datePicker = null;
    }
    super.disconnect();
  }

  public scrolled() {
    if (!this.datePicker || !this.datePicker.isOpen) {
      return;
    }
    // Throttle to one repositioning per animation frame regardless of scroll
    // event frequency.
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateCalendarPosition();
        this.ticking = false;
      });

      this.ticking = true;
    }
  }

  public isOpen() {
    return this.datePicker ? this.datePicker.isOpen : false;
  }

  public open() {
    if (this.datePicker) {
      this.datePicker.open();
    }
  }

  public close() {
    if (this.datePicker) {
      this.datePicker.destroy();
    }
  }

  public setDate(date: string | Date, triggerChange = true): void {
    if (this.datePicker) {
      this.datePicker.setDate(date, triggerChange);
    }
  }

  // Repositions the open calendar — needed when the calendar is anchored to
  // an input inside a scrolling modal/panel so it tracks the input on scroll.
  private updateCalendarPosition() {
    if (this.datePicker) {
      this.datePicker._positionCalendar();
    }
  }
}
